import { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { AccordionSection } from '@/components/ui/Accordion';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { usePracticeEntities } from '@/features/practice-entities/hooks/usePracticeEntities';
import { useTaxRates } from '@/features/settings/hooks/useTaxRates';
import {
  useBillingEntityConfigs,
  useCreateBillingEntityConfig,
  useUpdateBillingEntityConfig,
  type BillingEntityConfig,
  type UpdateBillingEntityConfigPayload,
} from '@/features/billing/hooks/useBillingEntityConfig';

/**
 * BillingSettings — Per-practice-entity billing configuration.
 *
 * Users select a practice entity from a dropdown, then view/edit its billing
 * configuration: document prefixes, numbering, tax defaults, payment terms,
 * overdue reminders, billing entity branding/contact info.
 */
export function BillingSettings() {
  const { toast } = useToast();
  // Source the dropdown from the REAL practice_entities table (not the
  // `practice_entities` system-lookup list). The billing entity-config endpoint
  // resolves the value against practice_entities, so offering a lookup code with
  // no matching entity row produced "Practice entity with code '…' not found".
  //
  // Do NOT filter by is_active here: billing configuration must remain available
  // for every configured practice entity. Filtering to active-only hid entities
  // that exist (and show on Settings -> Practice Entities) but happen to be
  // inactive, producing a false "No practice entities configured yet" message.
  const { data: practiceEntities, isLoading: entitiesLoading } = usePracticeEntities();
  const { data: configs } = useBillingEntityConfigs();
  const { data: taxRates } = useTaxRates();
  const createMutation = useCreateBillingEntityConfig();
  const updateMutation = useUpdateBillingEntityConfig();

  // Selection is keyed by the practice entity UUID (always resolvable by the API).
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const selectedEntity = (practiceEntities || []).find((e) => e.id === selectedEntityId);
  // Match an existing config by entity id, falling back to code for configs
  // saved before id-keyed selection.
  const selectedConfig = (configs || []).find(
    (c) =>
      c.practiceEntityId === selectedEntityId ||
      (!!selectedEntity?.code && c.practiceEntityCode === selectedEntity.code),
  );

  // Form state
  const [form, setForm] = useState<FormState>(getDefaultForm());
  const [initialized, setInitialized] = useState(false);

  // Auto-select first entity
  useEffect(() => {
    if (!selectedEntityId && practiceEntities && practiceEntities.length > 0) {
      setSelectedEntityId(practiceEntities[0].id);
    }
  }, [practiceEntities, selectedEntityId]);

  // Populate form when selection changes
  useEffect(() => {
    if (selectedConfig) {
      setForm(configToForm(selectedConfig));
      setInitialized(true);
    } else if (selectedEntityId) {
      // No config exists yet — show defaults with the entity name pre-filled.
      const defaults = getDefaultForm();
      if (selectedEntity) defaults.billingEntityName = selectedEntity.name;
      setForm(defaults);
      setInitialized(true);
    } else {
      setInitialized(false);
    }
  }, [selectedConfig, selectedEntityId, selectedEntity]);

  const handleChange = (field: keyof FormState, value: string | number | number[] | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleReminderChange = (index: number, value: number) => {
    const updated = [...form.overdueReminderIntervals];
    updated[index] = value;
    handleChange('overdueReminderIntervals', updated);
  };

  const handlePaymentReminderChange = (index: number, value: number) => {
    const updated = [...form.paymentReminderDays];
    updated[index] = value;
    handleChange('paymentReminderDays', updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload: UpdateBillingEntityConfigPayload = {
      invoice_prefix: form.invoicePrefix,
      invoice_next_number: form.invoiceNextNumber,
      credit_note_prefix: form.creditNotePrefix,
      debit_note_prefix: form.debitNotePrefix,
      write_off_prefix: form.writeOffPrefix,
      discount_prefix: form.discountPrefix,
      quote_prefix: form.quotePrefix,
      default_tax_rate_id: form.defaultTaxRateId || null,
      default_tax_type: form.defaultTaxType || null,
      default_payment_terms_days: form.defaultPaymentTermsDays,
      default_quote_expiry_days: form.defaultQuoteExpiryDays,
      overdue_reminder_intervals: form.overdueReminderIntervals,
      payment_reminder_days: form.paymentReminderDays,
      recurring_advance_days: form.recurringAdvanceDays,
      billing_entity_name: form.billingEntityName || null,
      billing_entity_address: form.billingEntityAddress || null,
      billing_entity_email: form.billingEntityEmail || null,
      billing_entity_phone: form.billingEntityPhone || null,
      billing_entity_business_number: form.billingEntityBusinessNumber || null,
      payment_instructions: form.paymentInstructions || null,
      show_tax_breakdown: form.showTaxBreakdown,
      show_due_date: form.showDueDate,
      require_due_date: form.requireDueDate,
      rounding_precision: form.roundingPrecision,
      currency_symbol: form.currencySymbol,
      quantity_label_mode: form.quantityLabelMode as 'hours' | 'quantity',
      fiscal_year_start: form.fiscalYearStart || null,
    };

    try {
      if (selectedConfig) {
        await updateMutation.mutateAsync({
          practiceEntityId: selectedConfig.practiceEntityId,
          data: payload,
        });
        toast({ variant: 'success', title: 'Billing configuration updated' });
      } else {
        // Create new config for this practice entity (keyed by UUID).
        await createMutation.mutateAsync({
          practice_entity_id: selectedEntityId,
          ...payload,
        });
        toast({ variant: 'success', title: 'Billing configuration created' });
      }
    } catch {
      toast({ variant: 'error', title: 'Failed to save billing configuration' });
    }
  };

  const isLoading = entitiesLoading;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
        </div>
      </Card>
    );
  }

  const entityOptions = (practiceEntities || []).map((e) => ({
    value: e.id,
    label: e.name,
  }));

  // Show which entities already have config (matched by entity id).
  const configuredEntityIds = new Set(
    (configs || []).map((c) => c.practiceEntityId).filter(Boolean),
  );

  if (entityOptions.length === 0) {
    return (
      <Card>
        <CardHeader
          title="Billing Configuration"
          description="Configure billing settings per practice entity."
        />
        <CardContent>
          <div className="py-8 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No practice entities configured yet. Please add a practice entity in Settings &rarr;
              Practice Entities first.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Practice Entity Selector */}
      <Card>
        <CardHeader
          title="Billing Configuration"
          description="Configure billing settings per practice entity. Each entity can have its own numbering, tax defaults, and branding."
        />
        <CardContent>
          <div className="max-w-sm">
            <Select
              label="Practice Entity"
              value={selectedEntityId}
              onChange={(e) => setSelectedEntityId(e.target.value)}
              options={entityOptions.map((o) => ({
                ...o,
                label: `${o.label}${configuredEntityIds.has(o.value) ? '' : ' (not configured)'}`,
              }))}
              placeholder="Select entity..."
            />
          </div>
        </CardContent>
      </Card>

      {selectedEntityId &&
        (initialized ? (
          <>
            {/* Document Numbering */}
            <AccordionSection
              title="Document Numbering"
              description="Prefixes and starting numbers for billing documents."
              defaultOpen
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Input
                  label="Invoice Prefix"
                  value={form.invoicePrefix}
                  onChange={(e) => handleChange('invoicePrefix', e.target.value)}
                  maxLength={10}
                />
                <Input
                  label="Starting Number"
                  type="number"
                  value={String(form.invoiceNextNumber)}
                  onChange={(e) => handleChange('invoiceNextNumber', Number(e.target.value))}
                  min={1}
                />
                <Input
                  label="Quote Prefix"
                  value={form.quotePrefix}
                  onChange={(e) => handleChange('quotePrefix', e.target.value)}
                  maxLength={10}
                />
                <Input
                  label="Credit Note Prefix"
                  value={form.creditNotePrefix}
                  onChange={(e) => handleChange('creditNotePrefix', e.target.value)}
                  maxLength={10}
                />
                <Input
                  label="Debit Note Prefix"
                  value={form.debitNotePrefix}
                  onChange={(e) => handleChange('debitNotePrefix', e.target.value)}
                  maxLength={10}
                />
                <Input
                  label="Write-off Prefix"
                  value={form.writeOffPrefix}
                  onChange={(e) => handleChange('writeOffPrefix', e.target.value)}
                  maxLength={10}
                />
                <Input
                  label="Discount Prefix"
                  value={form.discountPrefix}
                  onChange={(e) => handleChange('discountPrefix', e.target.value)}
                  maxLength={10}
                />
              </div>
            </AccordionSection>

            {/* Tax & Payment Terms */}
            <AccordionSection
              title="Tax & Payment Terms"
              description="Default tax rate and payment terms for this entity. The selected tax rate will be pre-applied to all invoices created under this practice entity."
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Select
                  label="Financial Year Start"
                  value={form.fiscalYearStart}
                  onChange={(e) => handleChange('fiscalYearStart', e.target.value)}
                  options={[
                    { value: '', label: 'Use firm default' },
                    { value: '01-01', label: 'January 1' },
                    { value: '02-01', label: 'February 1' },
                    { value: '03-01', label: 'March 1' },
                    { value: '04-01', label: 'April 1' },
                    { value: '05-01', label: 'May 1' },
                    { value: '06-01', label: 'June 1' },
                    { value: '07-01', label: 'July 1' },
                    { value: '08-01', label: 'August 1' },
                    { value: '09-01', label: 'September 1' },
                    { value: '10-01', label: 'October 1' },
                    { value: '11-01', label: 'November 1' },
                    { value: '12-01', label: 'December 1' },
                  ]}
                  hint="When this entity's financial year begins"
                />
                <Select
                  label="Default Tax Rate"
                  value={form.defaultTaxRateId}
                  onChange={(e) => {
                    const selectedRate = (taxRates || []).find((r) => r.id === e.target.value);
                    handleChange('defaultTaxRateId', e.target.value);
                    if (selectedRate)
                      handleChange('defaultTaxType', selectedRate.name.toLowerCase());
                  }}
                  options={[
                    { value: '', label: 'No default (auto-resolve from client province)' },
                    ...(taxRates || [])
                      .filter((r) => r.isActive)
                      .map((r) => ({
                        value: r.id,
                        label: `${r.name} (${(r.rate < 1 ? r.rate * 100 : r.rate).toFixed(2)}%)${r.province ? ` — ${r.province}` : ''}`,
                      })),
                  ]}
                  hint="Invoices under this entity will use this rate by default"
                />
                <Input
                  label="Default Payment Terms (days)"
                  type="number"
                  value={String(form.defaultPaymentTermsDays)}
                  onChange={(e) => handleChange('defaultPaymentTermsDays', Number(e.target.value))}
                  min={0}
                  max={365}
                />
                <Input
                  label="Quote Expiry (days)"
                  type="number"
                  value={String(form.defaultQuoteExpiryDays)}
                  onChange={(e) => handleChange('defaultQuoteExpiryDays', Number(e.target.value))}
                  min={0}
                  max={365}
                  hint="Quotes will expire this many days after the issue date"
                />
                {/* <Input
                  label="Recurring Advance (days)"
                  type="number"
                  value={String(form.recurringAdvanceDays)}
                  onChange={(e) => handleChange('recurringAdvanceDays', Number(e.target.value))}
                  min={1}
                  max={30}
                  hint="Days before billing date to generate invoice"
                /> */}
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Apply Rounding
                  </label>
                  <select
                    value={form.roundingPrecision}
                    onChange={(e) =>
                      handleChange('roundingPrecision', parseInt(e.target.value, 10))
                    }
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  >
                    <option value={0}>No rounding</option>
                    <option value={10}>Round to nearest $10</option>
                    <option value={100}>Round to nearest $100</option>
                    <option value={1000}>Round to nearest $1,000</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Round invoice totals to the nearest unit.
                  </p>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Currency Symbol
                  </label>
                  <input
                    type="text"
                    value={form.currencySymbol}
                    onChange={(e) => handleChange('currencySymbol', e.target.value.slice(0, 5))}
                    maxLength={5}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    placeholder="$"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Displayed on invoices (e.g. $, £, €, ¥).
                  </p>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Line Item Quantity Label
                  </label>
                  <select
                    value={form.quantityLabelMode}
                    onChange={(e) => handleChange('quantityLabelMode', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  >
                    <option value="hours">Hours (Hrs)</option>
                    <option value="quantity">Quantity (Qty)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Display &quot;Hrs&quot; or &quot;Qty&quot; on billing screens.
                  </p>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={form.showTaxBreakdown}
                    onChange={(e) => handleChange('showTaxBreakdown', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Show tax breakdown on invoices
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={form.showDueDate}
                    onChange={(e) => handleChange('showDueDate', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Show due date on invoices
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={form.requireDueDate}
                    onChange={(e) => handleChange('requireDueDate', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Due date is mandatory on invoices
                </label>
              </div>
            </AccordionSection>

            {/* Reminder schedules — pre-due and overdue, shown side by side.
              Each practice entity uses its own configured days. */}
            <AccordionSection
              title="Payment & Overdue Reminders"
              description="Reminder schedules for this practice entity — before and after the invoice due date."
            >
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <Card padding="none" className="border-0 shadow-none dark:bg-transparent">
                  <CardHeader
                    title="Payment Reminders (Before Due)"
                    description="Days before the due date when upcoming-payment reminders are sent."
                  />
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      {form.paymentReminderDays.map((interval, index) => (
                        <Input
                          key={index}
                          type="number"
                          value={String(interval)}
                          onChange={(e) =>
                            handlePaymentReminderChange(index, Number(e.target.value))
                          }
                          min={1}
                          max={365}
                          className="w-24"
                        />
                      ))}
                    </div>
                    <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                      Reminders sent to the client and the invoice partner these many days before
                      the due date.
                    </p>
                  </CardContent>
                </Card>

                <Card padding="none" className="border-0 shadow-none dark:bg-transparent">
                  <CardHeader
                    title="Overdue Reminders (After Due)"
                    description="Days after due date when reminders are sent."
                  />
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      {form.overdueReminderIntervals.map((interval, index) => (
                        <Input
                          key={index}
                          type="number"
                          value={String(interval)}
                          onChange={(e) => handleReminderChange(index, Number(e.target.value))}
                          min={1}
                          max={365}
                          className="w-24"
                        />
                      ))}
                    </div>
                    <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                      Reminders sent at these intervals after the invoice due date.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </AccordionSection>

            {/* Billing Entity Details */}
            <AccordionSection
              title="Billing Entity Details"
              description="Business details shown on invoices and billing documents for this entity."
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="Entity Name"
                  value={form.billingEntityName}
                  onChange={(e) => handleChange('billingEntityName', e.target.value)}
                  placeholder="Your Firm Inc."
                />
                <Input
                  label="Business Number"
                  value={form.billingEntityBusinessNumber}
                  onChange={(e) => handleChange('billingEntityBusinessNumber', e.target.value)}
                  placeholder="123456789RC0001"
                />
                <Input
                  label="Email"
                  type="email"
                  value={form.billingEntityEmail}
                  onChange={(e) => handleChange('billingEntityEmail', e.target.value)}
                  placeholder="billing@yourfirm.ca"
                />
                <Input
                  label="Phone"
                  type="tel"
                  value={form.billingEntityPhone}
                  onChange={(e) => handleChange('billingEntityPhone', e.target.value)}
                  placeholder="+1 (416) 555-0100"
                />
                <div className="sm:col-span-2">
                  <Input
                    label="Address"
                    value={form.billingEntityAddress}
                    onChange={(e) => handleChange('billingEntityAddress', e.target.value)}
                    placeholder="123 Bay Street, Suite 400, Toronto, ON M5J 2T3"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Payment Instructions
                  </label>
                  <textarea
                    value={form.paymentInstructions}
                    onChange={(e) => handleChange('paymentInstructions', e.target.value)}
                    placeholder="Payment instructions shown on invoices..."
                    rows={3}
                    className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                  />
                </div>
              </div>
            </AccordionSection>

            {/* Actions — always visible below the accordion sections. */}
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (selectedConfig) setForm(configToForm(selectedConfig));
                  else setForm(getDefaultForm());
                }}
              >
                Reset
              </Button>
              <Button type="submit" loading={isSaving}>
                {selectedConfig ? 'Save Changes' : 'Create Configuration'}
              </Button>
            </div>
          </>
        ) : (
          <Card>
            <CardContent>
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-gray-500">Loading configuration...</p>
              </div>
            </CardContent>
          </Card>
        ))}
    </form>
  );
}

// ─── Form State Helpers ──────────────────────────────────────────────────────

interface FormState {
  invoicePrefix: string;
  invoiceNextNumber: number;
  creditNotePrefix: string;
  debitNotePrefix: string;
  writeOffPrefix: string;
  discountPrefix: string;
  quotePrefix: string;
  defaultTaxRateId: string;
  defaultTaxType: string;
  defaultPaymentTermsDays: number;
  defaultQuoteExpiryDays: number;
  overdueReminderIntervals: number[];
  paymentReminderDays: number[];
  recurringAdvanceDays: number;
  billingEntityName: string;
  billingEntityAddress: string;
  billingEntityEmail: string;
  billingEntityPhone: string;
  billingEntityBusinessNumber: string;
  paymentInstructions: string;
  showTaxBreakdown: boolean;
  showDueDate: boolean;
  requireDueDate: boolean;
  roundingPrecision: number;
  currencySymbol: string;
  quantityLabelMode: string;
  fiscalYearStart: string;
}

function getDefaultForm(): FormState {
  return {
    invoicePrefix: 'INV-',
    invoiceNextNumber: 1001,
    creditNotePrefix: 'CN-',
    debitNotePrefix: 'DN-',
    writeOffPrefix: 'WO-',
    discountPrefix: 'DSC-',
    quotePrefix: 'QT-',
    defaultTaxRateId: '',
    defaultTaxType: 'hst',
    defaultPaymentTermsDays: 30,
    defaultQuoteExpiryDays: 30,
    overdueReminderIntervals: [1, 7, 14, 30],
    paymentReminderDays: [7, 3, 1],
    recurringAdvanceDays: 7,
    billingEntityName: '',
    billingEntityAddress: '',
    billingEntityEmail: '',
    billingEntityPhone: '',
    billingEntityBusinessNumber: '',
    paymentInstructions: '',
    showTaxBreakdown: true,
    showDueDate: true,
    requireDueDate: false,
    roundingPrecision: 0,
    currencySymbol: '$',
    quantityLabelMode: 'hours',
    fiscalYearStart: '',
  };
}

function configToForm(config: BillingEntityConfig): FormState {
  return {
    invoicePrefix: config.invoicePrefix || 'INV-',
    invoiceNextNumber: config.invoiceNextNumber || 1001,
    creditNotePrefix: config.creditNotePrefix || 'CN-',
    debitNotePrefix: config.debitNotePrefix || 'DN-',
    writeOffPrefix: config.writeOffPrefix || 'WO-',
    discountPrefix: config.discountPrefix || 'DSC-',
    quotePrefix: config.quotePrefix || 'QT-',
    defaultTaxRateId: config.defaultTaxRateId || '',
    defaultTaxType: config.defaultTaxType || 'hst',
    defaultPaymentTermsDays: config.defaultPaymentTermsDays ?? 30,
    defaultQuoteExpiryDays: config.defaultQuoteExpiryDays ?? 30,
    overdueReminderIntervals: config.overdueReminderIntervals ?? [1, 7, 14, 30],
    paymentReminderDays: config.paymentReminderDays ?? [7, 3, 1],
    recurringAdvanceDays: config.recurringAdvanceDays ?? 7,
    billingEntityName: config.billingEntityName || '',
    billingEntityAddress: config.billingEntityAddress || '',
    billingEntityEmail: config.billingEntityEmail || '',
    billingEntityPhone: config.billingEntityPhone || '',
    billingEntityBusinessNumber: config.billingEntityBusinessNumber || '',
    paymentInstructions: config.paymentInstructions || '',
    showTaxBreakdown: config.showTaxBreakdown ?? true,
    showDueDate: config.showDueDate ?? true,
    requireDueDate: config.requireDueDate ?? false,
    roundingPrecision: config.roundingPrecision ?? 0,
    currencySymbol: config.currencySymbol || '$',
    quantityLabelMode: config.quantityLabelMode || 'hours',
    fiscalYearStart: config.fiscalYearStart || '',
  };
}
